// HubPlusStatusPanel — READ-ONLY summary of the franchisee's current
// +HUB subscription state, rendered inside the consolidated PORTAL
// section on the HQ Admin franchisee detail page.
//
// Turn 5 (Feb 2026, Sam's directive):
//   * Purely informational for HQ at this stage.
//   * No manual billing controls, no Xero buttons, no subscription
//     overrides, no admin tooling.
//   * Reads only the fields already served by GET /franchisees/{id};
//     no new API calls.
//   * Renders cleanly for franchisees with no +HUB record at all.
//
// The state labels + colour tokens are aligned with the derivation
// module (`hub_plus_entitlement.py`) so nothing drifts between the
// visual chip HQ sees here and the entitlement rules the portal
// applies. Complimentary / Active / Cancelled_pending / Cancelled /
// Payment_issue / Pending / Expired / Not enrolled — each gets its
// own colour + one-line explainer so the distinction is obvious.
import { CheckCircle2, XCircle, Clock, AlertTriangle, MinusCircle, Sparkles } from "lucide-react";

const money = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

// ---------------------------------------------------------------------
// Derived status resolver — mirrors the effective states from the
// backend entitlement module. Kept small and pure so it can never
// call an API by accident.
// ---------------------------------------------------------------------
function resolveStatus(f) {
  const status = f?.hub_plus_status;
  const compEnds = f?.complimentary_ends_at ? new Date(f.complimentary_ends_at) : null;
  const cancelEff = f?.hub_plus_cancellation_effective_at
    ? new Date(f.hub_plus_cancellation_effective_at)
    : null;
  const now = new Date();
  const compOpen = compEnds ? compEnds > now : (status === "complimentary" || status === "pending");
  const noticeLive = cancelEff ? cancelEff > now : status === "cancelled_pending";

  if (!status && !f?.complimentary_started_at && !f?.complimentary_ends_at) {
    return {
      key: "no_scheme",
      label: "Not enrolled",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200", dot: "bg-slate-400" },
      icon: MinusCircle,
      blurb: "This franchisee has not been enrolled on the +HUB scheme.",
    };
  }
  if (status === "complimentary" && compOpen) {
    return {
      key: "complimentary",
      label: "Complimentary",
      tone: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", dot: "bg-amber-500" },
      icon: Sparkles,
      blurb: "Inside the free complimentary access window. All four +HUB modules are available.",
    };
  }
  if (status === "complimentary" && !compOpen) {
    return {
      key: "expired",
      label: "Expired",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
      icon: XCircle,
      blurb: "Complimentary window has ended without a paid subscription. No +HUB modules available.",
    };
  }
  if (status === "pending" && compOpen) {
    return {
      key: "pending_comp",
      label: "Complimentary (activation pending)",
      tone: { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", dot: "bg-amber-500" },
      icon: Sparkles,
      blurb: "Paid selection confirmed. Complimentary access continues until the free window closes; billing activates after.",
    };
  }
  if (status === "pending") {
    return {
      key: "pending",
      label: "Pending activation",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
      icon: Clock,
      blurb: "Paid selection confirmed but activation flow has not completed. No +HUB modules available yet.",
    };
  }
  if (status === "active") {
    return {
      key: "active",
      label: "Active (paid)",
      tone: { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", dot: "bg-emerald-500" },
      icon: CheckCircle2,
      blurb: "Paid subscription running. Modules listed below are entitled.",
    };
  }
  if (status === "cancelled_pending" && noticeLive) {
    return {
      key: "cancelled_pending",
      label: "Cancelled — in notice",
      tone: { bg: "bg-orange-50", text: "text-orange-800", border: "border-orange-200", dot: "bg-orange-500" },
      icon: Clock,
      blurb: "30-day cancellation notice in progress. Access remains active until the effective date.",
    };
  }
  if (status === "cancelled_pending" && !noticeLive) {
    return {
      key: "cancelled",
      label: "Cancelled",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
      icon: XCircle,
      blurb: "Notice period elapsed. Access has ended.",
    };
  }
  if (status === "cancelled") {
    return {
      key: "cancelled",
      label: "Cancelled",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
      icon: XCircle,
      blurb: "Subscription cancelled. No +HUB modules available.",
    };
  }
  if (status === "payment_issue") {
    return {
      key: "payment_issue",
      label: "Payment issue (grace period)",
      tone: { bg: "bg-red-50", text: "text-red-800", border: "border-red-200", dot: "bg-red-500" },
      icon: AlertTriangle,
      blurb: "Last collection failed. Currently in a grace period — selected modules remain available. Suspension timing will be defined in the billing phase.",
    };
  }
  if (status === "expired") {
    return {
      key: "expired",
      label: "Expired",
      tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
      icon: XCircle,
      blurb: "Access has ended. No +HUB modules available.",
    };
  }
  return {
    key: "unknown",
    label: status || "Unknown",
    tone: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", dot: "bg-slate-500" },
    icon: MinusCircle,
    blurb: "Unrecognised state — check the raw hub_plus_status field.",
  };
}

const REASON_LABELS = {
  first_genuine_login: "First-login (60-day free trial)",
  existing_cohort: "Existing cohort (backfill)",
  manual_admin: "Manual grant by HQ",
};

const MODULE_LABELS = {
  territory_plus: "My Territory+",
  invoicing: "Invoicing+",
  marketing: "Marketing+",
  bookings: "Bookings+",
};

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm border-b border-stone-100 last:border-b-0">
      <span className="text-stone-500">{label}</span>
      <span className="font-medium text-stone-900 text-right" data-testid={testid}>
        {value || <span className="text-stone-400">—</span>}
      </span>
    </div>
  );
}

export default function HubPlusStatusPanel({ franchisee }) {
  const f = franchisee || {};
  const s = resolveStatus(f);
  const Icon = s.icon;
  const modules = Array.isArray(f.hub_plus_selected_modules)
    ? f.hub_plus_selected_modules
    : [];

  return (
    <div className="space-y-4" data-testid="hub-plus-status-panel">
      {/* Status chip + blurb */}
      <div className={`rounded-lg border ${s.tone.border} ${s.tone.bg} p-4`}>
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${s.tone.text}`} />
          <div className="flex-1 min-w-0">
            <div
              className={`text-sm font-bold ${s.tone.text}`}
              data-testid="hub-plus-status-label"
            >
              {s.label}
            </div>
            <div className="text-xs text-stone-600 mt-0.5">{s.blurb}</div>
          </div>
          <span
            className="text-[10px] uppercase tracking-wider font-bold text-stone-500 bg-white/70 border border-stone-200 rounded-full px-2 py-0.5"
            data-testid="hub-plus-raw-status"
          >
            {f.hub_plus_status || "unset"}
          </span>
        </div>
      </div>

      {/* Dates + reason grid — only shown when the franchisee has any
          +HUB fields to display (i.e. not "no_scheme"). */}
      {s.key !== "no_scheme" && (
        <div className="rounded-lg border border-stone-200 bg-white px-4 py-2">
          <Row
            label="Complimentary started"
            value={money(f.complimentary_started_at)}
            testid="hub-plus-comp-started"
          />
          <Row
            label="Complimentary ends"
            value={money(f.complimentary_ends_at)}
            testid="hub-plus-comp-ends"
          />
          <Row
            label="Start reason"
            value={REASON_LABELS[f.complimentary_start_reason] || f.complimentary_start_reason}
            testid="hub-plus-start-reason"
          />
          {(f.hub_plus_cancellation_notice_at || f.hub_plus_cancellation_effective_at) && (
            <>
              <Row
                label="Cancellation notice given"
                value={money(f.hub_plus_cancellation_notice_at)}
                testid="hub-plus-cancel-notice"
              />
              <Row
                label="Cancellation effective"
                value={money(f.hub_plus_cancellation_effective_at)}
                testid="hub-plus-cancel-effective"
              />
            </>
          )}
        </div>
      )}

      {/* Selected +HUB modules (only meaningful once activation
          happens — during complimentary the franchisee has ALL four,
          so we suppress the list to avoid confusion). */}
      {modules.length > 0 && s.key !== "complimentary" && s.key !== "pending_comp" && (
        <div className="rounded-lg border border-stone-200 bg-white px-4 py-3">
          <div className="text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">
            Selected +HUB modules
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="hub-plus-selected-modules">
            {modules.map((m) => (
              <span
                key={m}
                className="text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                data-testid={`hub-plus-module-chip-${m}`}
              >
                {MODULE_LABELS[m] || m}
              </span>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-stone-400 leading-snug">
        Read-only for HQ. Manual billing, Xero and subscription controls will land in the billing phase.
      </p>
    </div>
  );
}
